import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight } from "@phosphor-icons/react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden grain bg-ink flex items-center justify-center px-5 py-10">
      <div className="absolute inset-0 opacity-50">
        <img src="https://images.unsplash.com/photo-1657450717623-93270c89af8e?fm=jpg&q=85&w=1600&auto=format&fit=crop" alt="Lignum Vitae — Jamaica's national flower" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/75 to-ink" />
      </div>
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up overflow-hidden">
        {/* Right-side Jamaican vertical tricolor — black / green / gold */}
        <div className="absolute right-0 top-0 bottom-0 w-2 flex flex-col" aria-hidden>
          <div className="flex-1 bg-jam-black" />
          <div className="flex-1 bg-jam-green" />
          <div className="flex-1 bg-jam-gold" />
        </div>
        <div className="absolute right-2 top-8 bottom-8 w-px bg-cream/10" aria-hidden />
        <div className="flex items-center gap-3 mb-3">
          <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Giffwurldaunce</p>
          <div className="jam-stripe h-[3px] flex-1 rounded-full" aria-hidden />
        </div>
        <h1 className="text-cream text-4xl sm:text-5xl font-black tracking-tighter leading-none">Welcome<br/>back home.</h1>
        <p className="text-cream/60 mt-3 text-sm">Log in and feel the rhythm of the islands. 🇯🇲</p>
        <p className="text-cream/40 mt-1 text-[10px] tracking-[0.25em] uppercase">Lignum Vitae · Jamaica's national flower</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
          <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
          {err && <p data-testid="login-error" className="text-red-400 text-sm">{err}</p>}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Logging in…" : <>Enter the wave <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>

        <p className="mt-6 text-cream/60 text-sm text-center">
          New here?{" "}
          <Link to="/signup" data-testid="login-to-signup" className="text-papaya font-bold hover:underline">Create an account</Link>
        </p>
        <p className="mt-2 text-cream/40 text-xs text-center">
          <Link to="/forgot-password" data-testid="login-to-forgot" className="hover:text-papaya">Forgot password?</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors"
      />
    </label>
  );
}
